/**
 * ResumeTemplateDialog — create / edit a Resume RunTemplate (variant-scoped).
 *
 * Slice 1 of #1114 (meta #1112). Resume templates are a first-class
 * RunTemplate kind (kind='resume') stored alongside replay and generation
 * templates in the unified `runtemplates` collection. This dialog manages
 * the resume-specific fields:
 *   - phaseFilter — which phases to run (generate / execute / heal);
 *     defaults to all three (broadest resume)
 *   - Browser + timing — playwrightTimeout, assertionTimeout, captureTrace
 *   - Diagnostics — logLevel, testDataMode, reportConsoleErrors, reportNetworkErrors
 *
 * Mirrors the shape of `ReplayTemplateDialog` (same field density, same
 * import set) so the two editors stay symmetric as the dialog surface
 * grows. Does NOT contain the generate-mode-specific fields
 * (aiInstructions, phases, stabilityExpectation) — those live only on
 * generation templates.
 *
 * `maxTestCases` is NOT generate-only: resume batches its generate pass to
 * that slice size too, and the authoring dialog's resume mode exposes it as
 * "Max test cases (this pass)". This dialog simply doesn't offer it, so
 * templates created here keep the schema default of 1 (one case per pass).
 *
 * Architecture: this is a UI component; the RunTemplate write path goes
 * through `runTemplatesAPI.create/update`, not through the old
 * `applicationsAPI.update(appId, { templates })` pattern.
 */
import { useState } from 'react'
import { Info } from 'lucide-react'
import {
  Button,
  Label,
  Input,
  Select,
  Checkbox,
  Tooltip,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui'
import { runTemplatesAPI } from '@/lib/api'
import { BetweenTestsPauseRow } from '@/components/BetweenTestsPauseRow'
import { BetweenStepsPauseRow } from '@/components/BetweenStepsPauseRow'
import { useToast } from '@/hooks/useToast'
import { toUserMessage, extractRawDetails } from '@/lib/userMessage'
import { useRunTemplateLaunch } from '@/hooks/useRunTemplateLaunch'
import { RunTemplateLaunchFooter } from '@/components/RunTemplateLaunchFooter'
import type { RunTemplateSettings, UpdateRunTemplateInput, UnifiedRunTemplate, PipelinePhase, LogLevel } from '@/types'
import {
  DEFAULT_INTER_TEST_DELAY_MS,
  DEFAULT_PLAYWRIGHT_TIMEOUT_MS,
  DEFAULT_SLOW_MOTION_MS,
} from '@ottotester/shared-types'

/** The three phases a resume run can execute. Plan is handled by the
 * orchestrator's resume logic (it re-uses the existing plan, never
 * re-plans from scratch), so it is not surfaced as a user toggle here. */
const RESUME_PHASE_OPTIONS: ReadonlyArray<{ value: PipelinePhase; label: string }> = [
  { value: 'generate', label: 'Generate' },
  { value: 'execute', label: 'Execute' },
  { value: 'heal', label: 'Heal' },
]

export function ResumeTemplateDialog({
  template,
  variantId,
  applicationId,
  onClose,
  onSaved,
  launch,
}: {
  /** Existing resume template to edit, or null to create a new one. */
  template: UnifiedRunTemplate | null
  variantId: string
  /** Application id of the variant. Required at CREATE time so the
   * cascade on application delete (`runTemplateRepo.cascadeApplicationDelete`)
   * can find resume templates to drop. The backend schema does not
   * auto-derive this, so the UI must pass it explicitly. */
  applicationId: string
  /** Present when the dialog was opened by the Resume button rather than
   * from a list (#2175 slice 2). Swaps the footer for Run / Save and run.
   * Passing `settings` runs with what is on screen without saving it. */
  launch?: (settings?: Record<string, unknown>) => Promise<void> | void
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const [name, setName] = useState(template?.name || '')
  const [description, setDescription] = useState(template?.description || '')

  // phaseFilter — which phases resume runs execute. Empty / absent means
  // "all three" (the orchestrator's broadest resume). Default to all three
  // so new templates run the full resume pipeline out of the box.
  const [phaseFilter, setPhaseFilter] = useState<PipelinePhase[]>(
    () => template?.phaseFilter?.length ? template.phaseFilter : ['generate', 'execute', 'heal'],
  )

  // Timing — same unit conventions as ReplayTemplateDialog and
  // TestAuthoringTemplateDialog: UI surfaces in seconds, schema in ms.
  const [playwrightTimeout, setPlaywrightTimeout] = useState(
    (template?.playwrightTimeout ?? DEFAULT_PLAYWRIGHT_TIMEOUT_MS) / 1000,
  )
  // Coerce assertionTimeout from ms→s. Math.round handles legacy rows
  // saved under the old 500ms step (5500ms → 6s), matching the pattern
  // in the other two dialog files.
  const [assertionTimeout, setAssertionTimeout] = useState(
    Math.round((template?.assertionTimeout ?? 5000) / 1000),
  )
  // Pacing (#1642), stored in ms; between tests is shown in seconds.
  // 0 = no pause. A new template starts with the shared defaults; an
  // existing one shows what it stores, and a row without the field reads 0.
  const [interTestDelaySec, setInterTestDelaySec] = useState(
    Math.round((template ? template.interTestDelayMs ?? 0 : DEFAULT_INTER_TEST_DELAY_MS) / 1000),
  )
  const [slowMotionMs, setSlowMotionMs] = useState(
    template ? template.slowMotionMs ?? 0 : DEFAULT_SLOW_MOTION_MS,
  )
  const [captureTrace, setCaptureTrace] = useState<'off' | 'on-failure' | 'always'>(
    template?.captureTrace || 'on-failure',
  )
  const [logLevel, setLogLevel] = useState<LogLevel>(template?.logLevel || 'info')
  const [testDataMode, setTestDataMode] = useState<'off' | 'single-row' | 'all-rows'>(
    template?.testDataMode || 'off',
  )
  // Run-health reporting flags (#1009). Same shape + defaults as the
  // matching fields on the other two template editors so the operator's
  // mental model is symmetric across all three.
  const [reportConsoleErrors, setReportConsoleErrors] = useState(
    template?.reportConsoleErrors ?? false,
  )
  const [reportNetworkErrors, setReportNetworkErrors] = useState(
    template?.reportNetworkErrors ?? false,
  )
  const [isDefault, setIsDefault] = useState(template?.isDefault ?? false)
  const [saving, setSaving] = useState(false)

  function togglePhase(phase: PipelinePhase) {
    setPhaseFilter(prev =>
      prev.includes(phase) ? prev.filter(p => p !== phase) : [...prev, phase],
    )
  }

  /** Everything on screen that describes how the run behaves.
   *
   * Split out of `handleSave` (#2175 slice 2) so the Run button sends the
   * same object without writing it. `isDefault` is deliberately NOT here:
   * it says which template this is, not how the run goes, and it is one of
   * the fields a per-run override may not carry. */
  function buildSettings(): RunTemplateSettings {
    return {
      name: name.trim(),
      description: description.trim() || undefined,
      // Empty phaseFilter means "all phases" to the orchestrator. Send
      // the explicit list so the template accurately reflects intent.
      phaseFilter: phaseFilter.length ? phaseFilter : ['generate', 'execute', 'heal'],
      playwrightTimeout: playwrightTimeout * 1000,
      // s→ms, clamped to [1, 60] seconds — same guard as the other dialogs.
      assertionTimeout: Math.min(Math.max(Math.round(assertionTimeout) || 5, 1), 60) * 1000,
      // #1642 — s→ms, clamped to [0, 30] seconds (0 = no pause).
      interTestDelayMs: Math.min(Math.max(Math.round(interTestDelaySec) || 0, 0), 30) * 1000,
      // #1642 Slice 2 — per-action slow-motion (ms), already in ms.
      slowMotionMs: Math.max(Math.round(slowMotionMs) || 0, 0),
      captureTrace,
      logLevel,
      testDataMode,
      reportConsoleErrors,
      reportNetworkErrors,
    }
  }

  /** Returns true when the write landed — see the replay dialog for why
   * Save and run needs to know. */
  async function handleSave(): Promise<boolean> {
    if (!name.trim()) return false
    setSaving(true)
    try {
      const payload: UpdateRunTemplateInput = { ...buildSettings(), isDefault }
      if (template?.id) {
        await runTemplatesAPI.update(template.id, payload)
      } else {
        await runTemplatesAPI.create({
          ...payload,
          kind: 'resume',
          variantId,
          // applicationId is REQUIRED at create time. The
          // application-deletion cascade on the backend
          // (cascadeApplicationDelete) uses applicationId to find
          // resume/generation templates to drop; missing it leaves
          // orphan resume rows behind after the parent app is deleted.
          applicationId,
        })
      }
      onSaved()
      return true
    } catch (err) {
      toast({
        title: 'Failed to save resume template',
        description: toUserMessage(
          err,
          "Couldn't save the resume template. Try again — if the problem continues, check your connection.",
        ),
        rawDetails: extractRawDetails(err) ?? undefined,
        variant: 'error',
      })
      return false
    } finally {
      setSaving(false)
    }
  }

  // Only meaningful when opened by the Resume button; the stand-in keeps
  // the hook call unconditional.
  const launcher = useRunTemplateLaunch({
    buildSettings,
    save: handleSave,
    launch: launch ?? (() => {}),
  })

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Resume Template' : 'New Resume Template'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Default Resume Incomplete Work" />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description" />
          </div>

          {/* Phase filter — which phases to run on resume. All three
              default-selected for the broadest resume behavior. The user
              can narrow to e.g. "execute + heal" to skip regeneration of
              already-scripted tests. */}
          <div className="space-y-2">
            <Label className="text-xs">Resume phases</Label>
            <div className="flex gap-1">
              {RESUME_PHASE_OPTIONS.map(({ value, label }) => {
                const active = phaseFilter.includes(value)
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => togglePhase(value)}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      active
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Only processes test cases that were not completed in a previous run.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1.5">
                Page timeout (sec)
                <Tooltip
                  content="Maximum total time a single test can run before Playwright aborts it. Default 10."
                  triggerLabel="What is the page timeout?"
                  align="start"
                >
                  <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                </Tooltip>
              </Label>
              <Input
                type="number"
                min={1}
                max={300}
                value={playwrightTimeout}
                onChange={e => setPlaywrightTimeout(parseInt(e.target.value) || DEFAULT_PLAYWRIGHT_TIMEOUT_MS / 1000)}
                className="w-24"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1.5">
                Assertion timeout (sec)
                <Tooltip
                  content="Per-assertion polling timeout (await expect(...) calls). Tune up for slow-rendering apps. Default 5."
                  triggerLabel="What is the assertion timeout?"
                  align="end"
                >
                  <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                </Tooltip>
              </Label>
              <Input
                type="number"
                min={1}
                max={60}
                step={1}
                placeholder="5"
                value={assertionTimeout}
                onChange={e => {
                  // Accept any non-negative integer during typing — same
                  // pattern as the other two template dialogs.
                  const parsed = parseInt(e.target.value, 10)
                  setAssertionTimeout(Number.isFinite(parsed) && parsed >= 0 ? parsed : 0)
                }}
                onBlur={() => {
                  setAssertionTimeout(prev => Math.min(Math.max(prev || 5, 1), 60))
                }}
                className="w-28"
              />
            </div>
            {/* #1642 — pacing pair on its own row; tooltips open inward
                (tests left→start, steps right→end) so they don't clip. */}
            <BetweenTestsPauseRow value={interTestDelaySec} onChange={setInterTestDelaySec} align="start" />
            <BetweenStepsPauseRow value={slowMotionMs} onChange={setSlowMotionMs} align="end" />
            <div className="space-y-2">
              <Label className="text-xs">Trace capture</Label>
              <Select
                options={[
                  { value: 'off', label: 'Off' },
                  { value: 'on-failure', label: 'On failure (default)' },
                  { value: 'always', label: 'Always' },
                ]}
                value={captureTrace}
                onChange={e => setCaptureTrace(e.target.value as 'off' | 'on-failure' | 'always')}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Test data</Label>
              <Select
                options={[
                  { value: 'off', label: 'Off (default)' },
                  { value: 'single-row', label: 'Single row' },
                  { value: 'all-rows', label: 'All rows' },
                ]}
                value={testDataMode}
                onChange={e => setTestDataMode(e.target.value as 'off' | 'single-row' | 'all-rows')}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Activity log verbosity</Label>
            <Select
              options={[
                { value: 'info', label: 'Info (default)' },
                { value: 'debug', label: 'Debug' },
                { value: 'trace', label: 'Trace' },
              ]}
              value={logLevel}
              onChange={e => setLogLevel(e.target.value as LogLevel)}
            />
          </div>

          {/* Issue reporting section (#1009). Same shape as the other
              two editors for symmetric operator mental model. */}
          <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Issue reporting</Label>
            <label className="flex items-start gap-2">
              <Checkbox checked={reportConsoleErrors} onCheckedChange={setReportConsoleErrors} />
              <span className="text-sm leading-snug">
                Report console errors
                <span className="block text-xs text-muted-foreground">
                  Surface uncaught <code>pageError</code>, <code>console.error</code>, and <code>console.warn</code> captured during runs from this template.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <Checkbox checked={reportNetworkErrors} onCheckedChange={setReportNetworkErrors} />
              <span className="text-sm leading-snug">
                Report network errors
                <span className="block text-xs text-muted-foreground">
                  Surface HTTP 4xx / 5xx and failed network requests captured during runs from this template.
                </span>
              </span>
            </label>
          </div>

          <label className="flex items-center gap-2">
            <Checkbox checked={isDefault} onCheckedChange={setIsDefault} />
            <span className="text-sm">Set as default template</span>
          </label>
        </DialogBody>
        {launch ? (
          <RunTemplateLaunchFooter
            busy={saving || launcher.busy}
            canRun={!!name.trim()}
            onCancel={onClose}
            onRun={() => { void launcher.runNow() }}
            onSaveAndRun={() => { void launcher.saveAndRun() }}
          />
        ) : (
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={!name.trim() || saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
